import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Building2, CreditCard, Receipt, Inbox, Activity, FileText, StickyNote, CheckSquare, Square, Plus, Loader2 } from "lucide-react";
import { Card } from "@/components/common/PageHeader";
import { Loading, Stat } from "@/pages/_shared";
import { toast } from "sonner";
import api from "@/lib/api";

function Section({ icon: Icon, title, count, children, testid }) {
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="mb-3 flex items-center gap-2"><Icon size={15} className="text-[#1a5fd0]" /><span className="font-head text-[14px] font-bold text-slate-900">{title}</span>{count != null && <span className="rounded-full bg-slate-100 px-2 py-0.5 font-mono text-[10px] text-slate-500">{count}</span>}</div>
      {children}
    </Card>
  );
}

const Empty = ({ children }) => <div className="py-4 text-center text-[12px] text-slate-400">{children}</div>;

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [task, setTask] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => { api.get(`/crm/customer/${id}`).then(({ data }) => setData(data)).catch(() => setData(false)); }, [id]);
  useEffect(() => { load(); }, [load]);

  if (data === false) return <div className="animate-fade-up py-16 text-center text-[13px] text-slate-400">Customer not found. <Link to="/owner/customers" className="text-[#1a5fd0]">Back to Customers</Link></div>;
  if (!data) return <Loading />;
  const { customer, profile, credit_summary, accounts, payments, approvals, activity, documents, notes, tasks } = data;

  const addNote = async () => {
    if (!note.trim()) return; setBusy(true);
    try { await api.post(`/crm/customer/${id}/notes`, { text: note }); setNote(""); load(); toast.success("Note added"); }
    catch { toast.error("Could not add note."); } finally { setBusy(false); }
  };
  const addTask = async () => {
    if (!task.trim()) return; setBusy(true);
    try { await api.post(`/crm/customer/${id}/tasks`, { text: task }); setTask(""); load(); toast.success("Task added"); }
    catch { toast.error("Could not add task."); } finally { setBusy(false); }
  };
  const toggleTask = async (tid) => { try { await api.post(`/crm/tasks/${tid}/toggle`); load(); } catch { toast.error("Could not update task."); } };

  return (
    <div className="animate-fade-up space-y-6" data-testid="customer-detail-page">
      <button onClick={() => navigate("/owner/customers")} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-500 hover:text-slate-800"><ArrowLeft size={15} /> Customers & CRM</button>

      <Card className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-head text-[18px] font-bold text-slate-900" data-testid="customer-company">{customer.company_name || "—"}</div>
            <div className="mt-0.5 text-[13px] text-slate-600">{customer.name || "—"} · {customer.email}</div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-2 py-0.5 font-mono text-[9px] font-bold uppercase ${customer.role === "admin" ? "bg-[#b0862f]/10 text-[#b0862f]" : "bg-slate-100 text-slate-500"}`}>{customer.role}</span>
              <span className="font-mono text-[10px] text-slate-400">{customer.onboarding_complete ? "Onboarded" : "Onboarding in progress"}</span>
              <span className="font-mono text-[10px] text-slate-400">Joined {String(customer.created_at || "").slice(0, 10)}</span>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid gap-3 sm:grid-cols-4">
        <Stat label="Credit accounts" value={credit_summary.total} tone="slate" />
        <Stat label="Active" value={credit_summary.active} tone="green" />
        <Stat label="Action required" value={credit_summary.action_required} tone="red" />
        <Stat label="Payments" value={payments.length} tone="blue" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Section icon={Building2} title="Business profile" testid="crm-profile">
          {!profile ? <Empty>No business profile on file.</Empty> : (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
              {[["Legal name", profile.legal_name], ["Industry", profile.industry], ["State", profile.state], ["EIN", profile.ein_last4 ? `••${profile.ein_last4}` : null], ["Phone", profile.phone], ["Website", profile.website], ["Completeness", profile.completeness != null ? `${profile.completeness}%` : null], ["Next step", profile.next_step]].filter(([, v]) => v).map(([k, v]) => (
                <div key={k}><dt className="font-mono text-[9px] uppercase tracking-wider text-slate-400">{k}</dt><dd className="text-slate-700">{v}</dd></div>
              ))}
            </dl>
          )}
        </Section>

        <Section icon={CreditCard} title="Credit accounts" count={accounts.length} testid="crm-accounts">
          {accounts.length === 0 ? <Empty>No credit accounts yet.</Empty> : (
            <div className="space-y-1.5">{accounts.map((a, i) => (
              <div key={i} className="flex items-center justify-between text-[13px]"><span className="text-slate-700">{a.vendor}</span>
                <span className={`rounded-full px-2 py-0.5 font-mono text-[9px] uppercase ${a.bucket === "active" ? "bg-emerald-50 text-emerald-600" : a.bucket === "action" ? "bg-amber-50 text-amber-600" : "bg-slate-100 text-slate-500"}`}>{a.status}</span>
              </div>
            ))}</div>
          )}
        </Section>

        <Section icon={Receipt} title="Payments" count={payments.length} testid="crm-payments">
          {payments.length === 0 ? <Empty>No payments recorded.</Empty> : (
            <div className="space-y-1.5">{payments.map((p, i) => (
              <div key={i} className="flex items-center justify-between text-[13px]"><span className="text-slate-700">{p.amount != null ? `$${(p.amount / 100).toFixed(2)}` : "—"} <span className="text-slate-400">{(p.currency || "").toUpperCase()}</span></span><span className="font-mono text-[10px] text-slate-500">{p.status}</span></div>
            ))}</div>
          )}
        </Section>

        <Section icon={Inbox} title="Approvals" count={approvals.length} testid="crm-approvals">
          {approvals.length === 0 ? <Empty>No approvals for this customer.</Empty> : (
            <div className="space-y-1.5">{approvals.map((a) => (
              <div key={a.id} className="flex items-center justify-between text-[13px]"><span className="truncate text-slate-700">{a.title}</span><span className="ml-2 shrink-0 font-mono text-[9px] uppercase text-slate-500">{a.status}</span></div>
            ))}</div>
          )}
        </Section>

        <Section icon={FileText} title="Documents" count={documents.length} testid="crm-documents">
          {documents.length === 0 ? <Empty>No documents assigned. Assign from Documents & Applications.</Empty> : (
            <div className="space-y-1.5">{documents.map((d) => (
              <div key={d.id} className="flex items-center justify-between text-[13px]"><span className="truncate text-slate-700">{d.name}</span><span className="ml-2 shrink-0 font-mono text-[9px] text-slate-400">{String(d.when || "").slice(0, 10)}</span></div>
            ))}</div>
          )}
        </Section>

        <Section icon={Activity} title="AI activity" count={activity.length} testid="crm-activity">
          {activity.length === 0 ? <Empty>No AI activity yet.</Empty> : (
            <div className="space-y-1.5">{activity.map((r, i) => (
              <div key={i} className="text-[12px]"><span className="font-semibold text-slate-700">{r.agent}</span> <span className="text-slate-500">{r.task}</span><div className="font-mono text-[9px] text-slate-400">{new Date(r.when).toLocaleString()}</div></div>
            ))}</div>
          )}
        </Section>

        <Section icon={StickyNote} title="Notes" count={notes.length} testid="crm-notes">
          <div className="mb-3 flex gap-2">
            <input value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} data-testid="crm-note-input" placeholder="Add a note…" className="flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-[13px] outline-none focus:border-[#1a5fd0]" />
            <button onClick={addNote} disabled={busy} data-testid="crm-add-note-btn" className="rounded-lg bg-[#080d1a] px-3 py-1.5 text-white hover:bg-[#1a5fd0] disabled:opacity-60">{busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}</button>
          </div>
          {notes.length === 0 ? <Empty>No notes yet.</Empty> : (
            <div className="space-y-2">{notes.map((n) => (
              <div key={n.id} className="rounded-lg bg-slate-50 px-3 py-2 text-[13px] text-slate-700">{n.text}<div className="mt-0.5 font-mono text-[9px] text-slate-400">{n.author} · {new Date(n.when).toLocaleString()}</div></div>
            ))}</div>
          )}
        </Section>

        <Section icon={CheckSquare} title="Tasks" count={tasks.length} testid="crm-tasks">
          <div className="mb-3 flex gap-2">
            <input value={task} onChange={(e) => setTask(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTask()} data-testid="crm-task-input" placeholder="Add a task…" className="flex-1 rounded-lg border border-slate-200 px-3 py-1.5 text-[13px] outline-none focus:border-[#1a5fd0]" />
            <button onClick={addTask} disabled={busy} data-testid="crm-add-task-btn" className="rounded-lg bg-[#080d1a] px-3 py-1.5 text-white hover:bg-[#1a5fd0] disabled:opacity-60">{busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}</button>
          </div>
          {tasks.length === 0 ? <Empty>No tasks yet.</Empty> : (
            <div className="space-y-1.5">{tasks.map((t) => (
              <button key={t.id} onClick={() => toggleTask(t.id)} data-testid="crm-task-row" className="flex w-full items-center gap-2 text-left text-[13px]">
                {t.done ? <CheckSquare size={15} className="text-emerald-500" /> : <Square size={15} className="text-slate-300" />}
                <span className={t.done ? "text-slate-400 line-through" : "text-slate-700"}>{t.text}</span>
              </button>
            ))}</div>
          )}
        </Section>
      </div>
    </div>
  );
}
